/**
 * Memory Monitor Utility
 * Use this to track memory usage in real-time
 */

class MemoryMonitor {
  constructor() {
    this.measurements = [];
    this.startMemory = null;
    this.isMonitoring = false;
    this.intervalId = null;
  }

  start(intervalMs = 5000) {
    if (this.isMonitoring) {
      console.warn('Memory monitor already running');
      return;
    }

    if (!performance.memory) {
      console.warn('performance.memory not available in this browser');
      return;
    }

    this.isMonitoring = true;
    this.startMemory = performance.memory.usedJSHeapSize;
    
    console.log('📊 Memory Monitor Started');
    console.log(`Initial Memory: ${(this.startMemory / 1024 / 1024).toFixed(2)} MB`);

    this.intervalId = setInterval(() => {
      this.measure();
    }, intervalMs);
  }

  measure() {
    if (!performance.memory) return;

    const current = performance.memory.usedJSHeapSize;
    const total = performance.memory.totalJSHeapSize;
    const limit = performance.memory.jsHeapSizeLimit;
    
    const measurement = {
      timestamp: Date.now(),
      usedMB: (current / 1024 / 1024).toFixed(2),
      totalMB: (total / 1024 / 1024).toFixed(2),
      limitMB: (limit / 1024 / 1024).toFixed(2),
      percentUsed: ((current / limit) * 100).toFixed(2),
    };

    this.measurements.push(measurement);

    // Keep only last 50 measurements
    if (this.measurements.length > 50) {
      this.measurements.shift();
    }

    // Log warning if memory usage is high
    if (measurement.percentUsed > 70) {
      console.warn(`⚠️ High memory usage: ${measurement.usedMB} MB (${measurement.percentUsed}%)`);
    }

    // Log every measurement
    console.log(`📊 Memory: ${measurement.usedMB} MB / ${measurement.limitMB} MB (${measurement.percentUsed}%)`);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isMonitoring = false;
    
    if (performance.memory && this.startMemory) {
      const endMemory = performance.memory.usedJSHeapSize;
      const growth = endMemory - this.startMemory;
      console.log('📊 Memory Monitor Stopped');
      console.log(`Final Memory: ${(endMemory / 1024 / 1024).toFixed(2)} MB`);
      console.log(`Memory Growth: ${(growth / 1024 / 1024).toFixed(2)} MB`);
    }
  }

  getReport() {
    if (this.measurements.length === 0) {
      return 'No measurements recorded';
    }

    const first = this.measurements[0];
    const last = this.measurements[this.measurements.length - 1];
    const growth = last.usedMB - first.usedMB;

    return {
      duration: `${((last.timestamp - first.timestamp) / 1000).toFixed(0)} seconds`,
      startMemory: `${first.usedMB} MB`,
      endMemory: `${last.usedMB} MB`,
      growth: `${growth.toFixed(2)} MB`,
      measurements: this.measurements.length,
      avgMemory: `${(this.measurements.reduce((sum, m) => sum + parseFloat(m.usedMB), 0) / this.measurements.length).toFixed(2)} MB`,
    };
  }

  // Get DOM node count
  getDOMStats() {
    const allElements = document.getElementsByTagName('*');
    const videoElements = document.getElementsByTagName('video');
    const audioElements = document.getElementsByTagName('audio');
    const imageElements = document.getElementsByTagName('img');

    return {
      totalNodes: allElements.length,
      videoElements: videoElements.length,
      audioElements: audioElements.length,
      imageElements: imageElements.length,
    };
  }

  // Check for detached DOM nodes (requires Chrome DevTools)
  logDOMStats() {
    const stats = this.getDOMStats();
    console.log('🌳 DOM Stats:', stats);
    return stats;
  }
}

// Create singleton instance
const memoryMonitor = new MemoryMonitor();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.memoryMonitor = memoryMonitor;
}

export default memoryMonitor;

// Usage in console:
// window.memoryMonitor.start(5000)  // Start monitoring every 5 seconds
// window.memoryMonitor.getReport()  // Get current report
// window.memoryMonitor.logDOMStats() // Check DOM node count
// window.memoryMonitor.stop()       // Stop monitoring
